import type { Route } from './+types/services.move-in-out';
import { Link } from 'react-router';
import { Breadcrumbs, breadcrumbSchemaItems } from '~/components/breadcrumbs';
import { RelatedPages } from '~/components/related-pages';
import { buildMeta, schemaFor, breadcrumbSchema } from '~/lib/seo';
import { SITE, PRICING } from '~/data/site';
import { useBranding } from '~/lib/branding';

export const meta: Route.MetaFunction = () => [
  ...buildMeta({
    title: 'Move-In / Move-Out Cleaning | Tampa Bay',
    description:
      `Move-in and move-out cleaning in Tampa Bay. Empty house cleaning at ${PRICING.moveOutRate.toFixed(2)}/sqft flat rate. Walls excluded. ${PRICING.minimum} minimum. Schedule your move-in/out clean today.`,
    path: '/move-in-out',
    jsonLd: [
      schemaFor('/move-in-out'),
      breadcrumbSchema(breadcrumbSchemaItems('/move-in-out')),
    ].filter((s): s is Record<string, unknown> => !!s),
  }),
];

export default function MoveInOutServicePage() {
  const branding = useBranding();
  const examplePrice = 2400 * PRICING.moveOutRate;

  return (
    <main id="main-content">
      <Breadcrumbs path="/move-in-out" pageLabel="Move-In / Move-Out" />
      {/* ── HEADER ───────────────────────────────────────── */}
      <section className="section">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">One-Time Service</p>
          <h1 className="display-lg page-h1-full">
            Move-in and move-out,
            <em className="italic-display">a clean start or finish.</em>
          </h1>
          <p className="lede chapter-lede">
            Empty house cleaning for new chapters. Whether you&rsquo;re moving into your
            dream home or turning in the keys, we clean it like we&rsquo;d want to walk
            into it ourselves. Flat rate. No surprises. Walls excluded.
          </p>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">How much does it cost?</h2>
          <div className="price-example price-example-mt reveal">
            <h3 className="display-sm text-bone">${PRICING.moveOutRate.toFixed(2)} per square foot</h3>
            <div className="price-ex-line price-ex-block-mt">
              <span>Example: 2,400 sqft home<br /><small className="text-mute">Your actual price depends on your home&rsquo;s size. Walls excluded.</small></span>
              <span className="price-ex-total price-ex-total-sm">${Math.round(examplePrice)}</span>
            </div>
            <div className="price-ex-line">
              <span>Minimum on every service<br /><small className="text-mute">Every clean, no matter size, is professional work.</small></span>
              <span className="price-ex-total price-ex-total-sm">${PRICING.minimum}</span>
            </div>
            <div className="price-ex-line">
              <span>Walls not included<br /><small className="text-mute">Painting is their own job. We handle floors and fixtures.</small></span>
              <span className="price-ex-total price-ex-total-sm">—</span>
            </div>
            <p className="marginalia price-example-marginalia">
              See your exact price for your home in under 60 seconds.
            </p>
          </div>
          <div className="section-cta-left">
            <Link to="/quote" className="btn btn-brass">Calculate your price →</Link>
          </div>
        </div>
      </section>

      {/* ── MOVE-IN ───────────────────────────────────────── */}
      <section className="section-sm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Moving in</p>
          <h2 className="display-md">You deserve to walk into clean.</h2>
          <p className="lede chapter-lede">
            Moving day is chaos. You don&rsquo;t need to add cleaning to it. We come in
            before you arrive with boxes. Your new home is pristine. You walk in to a
            fresh start, not previous tenants&rsquo; dust.
          </p>
          <h3 className="feature-title mt-6">What we handle:</h3>
          <ul className="checklist mt-3">
            <li>Entire floor sweep and mop</li>
            <li>Kitchen cabinets (interior and exterior)</li>
            <li>All appliances (inside and outside)</li>
            <li>Bathrooms top-to-bottom</li>
            <li>Windows and sills</li>
            <li>Baseboards and trim</li>
            <li>Closets and shelving</li>
            <li>Ceiling fans and light fixtures</li>
            <li>Doors and door frames</li>
          </ul>
        </div>
      </section>

      {/* ── MOVE-OUT ──────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Moving out</p>
          <h2 className="display-md">Leave it better than you found it.</h2>
          <p className="lede chapter-lede">
            Your move-out cleaning is a chance to do right by the next people. We deep-clean
            every space. Your landlord gets a clean move-in inspection. The next family walks
            into a home you took care of.
          </p>
          <h3 className="feature-title mt-6">What we handle:</h3>
          <ul className="checklist mt-3">
            <li>Entire floor sweep and mop</li>
            <li>Kitchen cabinets (interior and exterior)</li>
            <li>All appliances (inside and outside)</li>
            <li>Bathrooms top-to-bottom</li>
            <li>Windows and sills</li>
            <li>Baseboards and trim</li>
            <li>Closets and shelving</li>
            <li>Ceiling fans and light fixtures</li>
            <li>Doors and door frames</li>
            <li>Behind and under appliances</li>
          </ul>
        </div>
      </section>

      {/* ── WALLS ────────────────────────────────────────– */}
      <section className="section-sm">
        <div className="container-narrow">
          <h2 className="display-md">About walls.</h2>
          <p className="lede chapter-lede">
            Walls are the painter&rsquo;s job, not ours. Our cleaning price covers floors,
            fixtures, cabinets, appliances, and trim. If you need wall repairs or painting,
            that&rsquo;s a separate contract. We make sure everything else is immaculate.
          </p>
        </div>
      </section>

      {/* ── WHY NLC ────────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Why {branding.name}</p>
          <h2 className="display-md">For the transition <em className="italic-display">that matters.</em></h2>
          <div className="value-props reveal">
            <div className="value-prop">
              <h3>W-2 Employees Only</h3>
              <p>Your crew is employed by us. Trained. Accountable. Benefits included.</p>
            </div>
            <div className="value-prop">
              <h3>Insured &amp; Background Checked</h3>
              <p>Every technician vetted. Insurance covers your home. You have recourse.</p>
            </div>
            <div className="value-prop">
              <h3>Flat Rate, No Surprises</h3>
              <p>${PRICING.moveOutRate.toFixed(2)}/sqft. That&rsquo;s it. No hidden fees. The price covers everything except walls.</p>
            </div>
            <div className="value-prop">
              <h3>We Care About New Chapters</h3>
              <p>Moving in or out is emotional. We make sure it&rsquo;s one less thing to worry about.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <RelatedPages path="/move-in-out" />

      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <p className="cta-eyebrow">Ready for the move?</p>
          <h2>Book your move-in or move-out clean</h2>
          <p className="cta-lede">See your price for your home. No obligation. Takes 60 seconds.</p>
          <Link to="/quote" className="btn btn-brass btn-lg">Get your price →</Link>
        </div>
      </section>
    </main>
  );
}
